import React, { useEffect, useMemo, useState } from "react";

import MedicalAssistant from "components/MedicalAssistant/MedicalAssistant";
import Spacer from "components/Spacer/Spacer";
import Card from "components/visitCommon/Card";
import StepScreenContainer from "components/visitCommon/StepScreenContainer";
import RegularSelect from "components/radioButtonCard/RegularSelect";
import CheckBoxCard from "components/checkBoxCard/CheckBoxCard";
import SearchInputWithChipCard from "components/searchInputWithChip/SearchInputWithChipCard";
import RadioButtonCard from "components/radioButtonCard/RadioButtonCard";

import useModal from "hooks/useModal";
import useVisitStepChange from "hooks/useVisitStepChange";
import { useDermatologyVisitFlow } from "hooks/useVisitFlow";
import useUpdateDermatologyVisit from "hooks/visit/useUpdateDermatologyVisit";
import { dermatology_sub_steps } from "helpers/enum";
import scrollToBottom from "helpers/scrollToBottom";
import { useDispatch } from "react-redux";
import { dermaVisitFlowReducers } from "reducers/dermatology/dermaVisitFlowReducer";

import HistoryDebounceSearchModal from "./HistoryDebounceSearchModal";

const TOBACCO_FORMS = [
  "Cigarettes",
  "Vape",
  "Cannabis",
  "Chew",
  "Cigar",
  "Pipe",
];

function normalizeMedicalConditions(selected) {
  if (!Array.isArray(selected)) return [];
  return selected
    .filter(Boolean)
    .map((el) => {
      const category = el?.category ?? "Other";
      const disease = el?.disease ?? "";
      const id = el?.id ?? `${category}:${disease}`;
      return { ...el, category, disease, id };
    })
    .filter((el) => el?.disease?.trim()?.length);
}

function SocialHistory() {
  const dispatch = useDispatch();
  const { setShowModal } = useModal();
  const { rightButtonClickRef } = useVisitStepChange();

  const { incompleteData, subSteps } = useDermatologyVisitFlow();
  const { updateVisit, updatingVisit } = useUpdateDermatologyVisit();

  const screenValueFromStore =
    subSteps?.[dermatology_sub_steps.social_history]?.value;

  const [firstAnswer, setFirstAnswer] = useState("");
  const [secondAnswer, setSecondAnswer] = useState("");
  const [thirdAnswer, setThirdAnswer] = useState("");
  const [thirdYesAnswer, setThirdYesAnswer] = useState([]);
  const [fourthAnswer, setFourthAnswer] = useState("");
  const [fourthYesAnswer, setFourthYesAnswer] = useState([]);

  const isValid = useMemo(() => {
    const firstOk = firstAnswer?.length > 0;
    const secondOk = secondAnswer?.length > 0;
    const thirdOk = thirdAnswer?.length > 0;

    const tobaccoOk =
      thirdAnswer === "No"
        ? true
        : thirdAnswer === "Yes"
        ? thirdYesAnswer?.length > 0
        : false;

    const medicalOk =
      fourthAnswer === "None"
        ? true
        : fourthAnswer?.length
        ? fourthYesAnswer?.length > 0
        : false;

    return firstOk && secondOk && thirdOk && tobaccoOk && medicalOk;
  }, [firstAnswer, secondAnswer, thirdAnswer, thirdYesAnswer, fourthAnswer, fourthYesAnswer]);

  function hydrateFromSocialOrMedicalHistory(arr) {
    if (!Array.isArray(arr) || !arr?.length) return;

    arr.forEach((question) => {
      switch (question?.health_profile_section) {
        case "exercise":
          setFirstAnswer(
            question?.answer?.[0] + question?.answer?.slice(1)?.toLowerCase()
          );
          break;
        case "alcohol":
          setSecondAnswer(question?.answer ?? "");
          break;
        case "tobacco":
          setThirdAnswer(
            question?.answer?.[0] + question?.answer?.slice(1)?.toLowerCase()
          );
          setThirdYesAnswer(question?.form_of_tobacco ?? []);
          break;
        case "medical_history": {
          const none = question?.answer === "NO";
          setFourthAnswer(none ? "None" : "Yes");

          if (question?.selected_options?.length > 0) {
            const selectedOptions = [];
            question?.selected_options?.forEach((option) => {
              option?.diseases?.forEach((disease) => {
                selectedOptions.push({
                  category: option?.category,
                  disease,
                  id: `${option?.category}:${disease}`,
                });
              });
            });
            setFourthYesAnswer(normalizeMedicalConditions(selectedOptions));
          } else {
            setFourthYesAnswer([]);
          }
          break;
        }
        default:
          break;
      }
    });
  }

  useEffect(() => {
    // Hydrate from store (preferred), otherwise from API incompleteData.
    if (screenValueFromStore?.length) {
      hydrateFromSocialOrMedicalHistory(screenValueFromStore);
    } else if (incompleteData?.social_or_medical_history?.length) {
      hydrateFromSocialOrMedicalHistory(incompleteData?.social_or_medical_history);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      dermaVisitFlowReducers.social_history({
        isValid,
      })
    );
  }, [dispatch, isValid]);

  rightButtonClickRef.current = onRightButtonClick;

  function handleOnSelectThirdYesAnswer(el) {
    let final = [...thirdYesAnswer];
    if (final?.includes(el)) {
      setThirdYesAnswer(final.filter((elem) => elem !== el));
    } else {
      setThirdYesAnswer([...final, el]);
    }
  }

  function buildSelectedOptions() {
    const refined = [];
    normalizeMedicalConditions(fourthYesAnswer).forEach((el) => {
      const existingIndex = refined?.findIndex(
        (answer) => answer.category === el.category
      );
      if (existingIndex > -1) {
        refined[existingIndex].diseases.push(el?.disease);
      } else {
        refined.push({
          category: el.category,
          diseases: [el?.disease],
        });
      }
    });
    return refined;
  }

  function onRightButtonClick(_, nextAnimation) {
    const selected_options = fourthAnswer === "None" ? [] : buildSelectedOptions();

    const payloadValue = [
      {
        health_profile_section: "exercise",
        question: "Do you exercise regularly?",
        answer_type_boolean: true,
        answer: firstAnswer.toUpperCase(),
      },
      {
        health_profile_section: "alcohol",
        question: "Do you drink alcohol?",
        answer_type_boolean: false,
        answer: secondAnswer,
      },
      {
        health_profile_section: "tobacco",
        question: "Do you smoke?",
        answer_type_boolean: true,
        answer: thirdAnswer.toUpperCase(),
        form_of_tobacco: thirdAnswer === "Yes" ? thirdYesAnswer : [],
      },
      {
        health_profile_section: "medical_history",
        question: "Current medical conditions under treatment",
        answer_type_boolean: true,
        answer: fourthAnswer === "None" ? "NO" : "YES",
        selected_options,
      },
    ];

    updateVisit({
      payload: {
        case: {
          id: incompleteData?.id,
          social_or_medical_history: payloadValue,
          dermatology_steps: incompleteData?.dermatology_steps,
        },
      },
      change_current_screen: true,
      screen_to_complete: dermatology_sub_steps.social_history,
      callBack: () => {
        dispatch(
          dermaVisitFlowReducers.social_history({
            value: payloadValue,
          })
        );
        nextAnimation();
      },
    });
  }

  return (
    <StepScreenContainer align={"start"}>
      <MedicalAssistant
        content={
          <p>
            Before we proceed, please answer a few lifestyle and social history
            questions.
          </p>
        }
      />

      <Card>
        <RegularSelect
          label="Do you exercise regularly?"
          options={["Yes", "No"]}
          selected={firstAnswer}
          setSelected={setFirstAnswer}
          orange={true}
        />

        <Spacer height={24} />

        <RegularSelect
          label="Do you drink alcohol?"
          options={["Never", "Occasionally", "Daily", "Previously/Quit"]}
          selected={secondAnswer}
          setSelected={setSecondAnswer}
          orange={true}
          mobile={true}
        />

        <Spacer height={24} />

        <RegularSelect
          label="Do you smoke?"
          options={["Yes", "No"]}
          selected={thirdAnswer}
          setSelected={(el) => {
            setThirdAnswer(el);
            if (el === "No") {
              setThirdYesAnswer([]);
            }
          }}
          orange={true}
        />

        {thirdAnswer === "Yes" ? (
          <div>
            <Spacer height={20} />
            <CheckBoxCard
              options={TOBACCO_FORMS}
              selected={thirdYesAnswer}
              onSelect={handleOnSelectThirdYesAnswer}
              variant="small"
            />
          </div>
        ) : null}

        <Spacer height={24} />

        <p className="font-medium text-2xl">
          Please add any medical conditions for which you are under treatment:
        </p>

        <Spacer height={16} />

        <SearchInputWithChipCard
          inputLabel={"Search Medical Conditions"}
          onClickInput={() => {
            if (updatingVisit) return;
            setFourthAnswer("Yes");
            scrollToBottom();
            setShowModal({
              state: true,
              children: (_, onClose) => {
                return (
                  <HistoryDebounceSearchModal
                    keyToSearch={"medical-history-options"}
                    title={"Search Medical Conditions"}
                    onClose={onClose}
                    selected={normalizeMedicalConditions(fourthYesAnswer)}
                    setSelected={(next) =>
                      setFourthYesAnswer(normalizeMedicalConditions(next))
                    }
                  />
                );
              },
              data: null,
            });
          }}
          selected={normalizeMedicalConditions(fourthYesAnswer)}
          setSelected={(next) =>
            setFourthYesAnswer(normalizeMedicalConditions(next))
          }
        />

        <Spacer height={16} />

        <RadioButtonCard
          options={["None"]}
          selected={fourthAnswer}
          onSelect={() => {
            if (fourthAnswer === "None") {
              setFourthAnswer("Yes");
            } else {
              setFourthAnswer("None");
              setFourthYesAnswer([]);
            }
          }}
        />
      </Card>
    </StepScreenContainer>
  );
}

export default SocialHistory;
